import { useCallback, useState, type FC } from 'react'
import Icon from '../../common/Icon'
import type { MediaFile } from '@/features/media-file/domain/models/media-file.ts'
import { formatSecondsToMinutesSeconds } from '@/utils/formatters.ts'
import { getMediaFileCategoryColor } from './get-media-file-category-color.tsx'
import Dragger from '@/components/common/Dragger.tsx'
import ContextMenu from '@/components/common/ContextMenu.tsx'
import DeleteFileConfirmationDialog from './DeleteFileConfirmationDialog.tsx'
import { MediaFileService } from '@/features/media-file/data/media-file.service.ts'
import { getAudioBufferFromAudioUrl } from '@/utils/get-audio-buffer-from-audio-url.ts'
import PlayButton from '@/components/common/PlayButton.tsx'
import { addDraggingAudioClip } from '@/features/clips/domain/use-cases/add-dragging-audio-clip.ts'
import { TrackType } from '@/shared/store/tracks.store.tsx'
import type { UUID } from '@/features/shared/data/string.types.ts'

export interface MediaFileBoxProps {
  file: MediaFile
  folderId: UUID
}

const MediaFileBox: FC<MediaFileBoxProps> = ({
  file: { id, duration, category, name },
  folderId,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)

  const categoryColorClass = getMediaFileCategoryColor(category.name)

  const fetchSound = useCallback(async () => {
    const mediaUrl = await MediaFileService.getFileUrl(id)
    return await getAudioBufferFromAudioUrl(mediaUrl)
  }, [id])

  const handleDragStart = useCallback(async () => {
    if (!duration) return
    const mediaUrl = await MediaFileService.getFileUrl(id)
    // Create a fetchSound function that uses the already-fetched URL to avoid redundant calls
    const fetchSoundWithUrl = async () => {
      return await getAudioBufferFromAudioUrl(mediaUrl)
    }
    addDraggingAudioClip(
      id,
      name,
      duration,
      TrackType.MediaFiles,
      fetchSoundWithUrl,
      mediaUrl
    )
  }, [id, name, duration])

  return (
    <>
      <DeleteFileConfirmationDialog
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        fileId={id}
        folderId={folderId}
      />
      <Dragger onDragStart={handleDragStart}>
        <div className="flex w-full max-w-[452px] cursor-pointer flex-col items-start justify-start gap-3 rounded-r-sm bg-zinc-700 p-4 shadow-[0px_0px_8px_-2px_rgba(0,0,0,0.20)]">
          <div className="flex w-full items-center justify-between gap-2">
            <p className="max-w-[326px] truncate text-lg font-semibold leading-7 text-white">
              {name}
            </p>
            <div className="flex items-center gap-2">
              <div className="text-right text-sm font-normal leading-tight text-neutral-400">
                {duration && formatSecondsToMinutesSeconds(duration)}
              </div>
              <PlayButton soundId={id} fetchBuffer={fetchSound} />
            </div>
          </div>
          <div className="flex w-full items-center justify-between gap-2">
            {category.id ? (
              <div
                className={`${categoryColorClass} flex h-5 items-center rounded-sm px-2 text-xs font-semibold capitalize`}
              >
                {category.name}
              </div>
            ) : (
              <div />
            )}
            <div className="relative">
              <button
                type="button"
                className="relative flex min-h-6 min-w-6 items-center justify-center gap-0.5"
                onClick={() => setIsDropdownOpen(true)}
              >
                <Icon name="menuHorizontal" />
              </button>
              <ContextMenu
                viewModel={[
                  {
                    key: 'ls_generic_delete',
                    action: () => setShowDeleteConfirmation(true),
                    icon: <Icon name="xMark" />,
                  },
                ]}
                isOpen={isDropdownOpen}
                setIsOpen={setIsDropdownOpen}
                position={{
                  right: 0,
                  top: 28,
                }}
              />
            </div>
          </div>
        </div>
      </Dragger>
    </>
  )
}

export default MediaFileBox
